import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextareaAutosize,
  TextField,
  Typography,
} from '@material-ui/core';
import { green, red } from '@material-ui/core/colors';
import Paper from '@material-ui/core/Paper';
import AddBoxIcon from '@material-ui/icons/AddBox';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import DoneIcon from '@material-ui/icons/Done';
import { AxiosResponse, Canceler } from 'axios';
import { flow } from 'lodash';
import moment, { DurationInputArg2 } from 'moment-timezone';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FormTextField from '../../../components/FormTextField';
import { Help } from '../../../components/Help';
import SearchTextField from '../../../components/SearchTextField';
import { SubmitControl } from '../../../components/SubmitControl';
import useAlertDialog from '../../../hooks/useAlertDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { getDistinctFlowKeys } from '../../../services/api/Onboarding';
import {
  editUserSegment,
  getAutoUpdateUserSegmentQueryResult,
  getUserSegment,
  getUserSegmentCuration,
  saveUserSegment,
  saveUserSegmentCuration,
  searchUserSegments,
} from '../../../services/api/Segment';
import {
  HomeType,
  IHomeCuration,
  IntervalType,
  ISegmentCuration,
  ISegmentCurationCollection,
  ISegmentCurationFeaturedCard,
  ISegmentCurationType,
  IUserSegment,
  IUserSegmentAutoUpdate,
  IUserSegmentAutoUpdateOption,
  IUserSegmentTrigger,
  IUserSegmentWebhook,
  TriggerType,
  UserSegmentBrazeTriggerType,
  UserSegmentPersistentType,
  UserSegmentType,
} from '../../../services/types';
import { convertPeriodWithTimezone, getNextFireDate } from '../../../services/utils';
import CurationView from './CurationView';
import useStyles from './styles';

interface IProps {
  segmentId?: number;
  onCreated?: (data: IUserSegmentAutoUpdate | IUserSegmentTrigger | IUserSegmentWebhook) => void;
  onError?: (error: Error) => void;
}

const homeTypeCounter = (data: IHomeCuration) => {
  const defaults =
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.FeaturedCards).length +
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.FictionSerials).length;

  const audios =
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.AudioFeatured).length +
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.AudioCollections).length;

  const texts =
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.TextFeatured).length +
    data.homeCurations.filter(curation => curation.type === ISegmentCurationType.TextCollections).length;

  return { [HomeType.ALL]: defaults, [HomeType.READ]: texts, [HomeType.LISTEN]: audios };
};

const getMaximum = (obj: Record<string, number>) => Object.keys(obj).reduce((a, b) => (obj[a] >= obj[b] ? a : b));

// const getHomeType = (key: HomeType) => HomeType[key];

const getHomeTypeFilters = (homeType: HomeType | undefined) => {
  switch (homeType) {
    case HomeType.LISTEN:
      return { FeaturedCardsFilter: ISegmentCurationType.AudioFeatured, CollectionsFilter: ISegmentCurationType.AudioCollections };
    case HomeType.READ:
      return { FeaturedCardsFilter: ISegmentCurationType.TextFeatured, CollectionsFilter: ISegmentCurationType.TextCollections };
    default:
      return { FeaturedCardsFilter: ISegmentCurationType.FeaturedCards, CollectionsFilter: ISegmentCurationType.FictionSerials };
  }
};

const CreateUserSegment = (props: IProps): JSX.Element => {
  // uses
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const alert = useAlertDialog();

  // consts
  const pathMatch = location.pathname.match(/v\/(\d+)\/?(\w+)?\/?(\w+)?/);
  const { segmentId, onCreated, onError } = props;

  // refs
  const originalData = useRef<IUserSegment>({
    name: '',
    description: '',
    type: UserSegmentType.AutoUpdate,
    daysToExpiration: '0',
    autoupdateOption: {
      sql: '',
      intervalType: IntervalType.Daily,
      intervalValue: 0,
      period: 0,
      persistentType: UserSegmentPersistentType.Long,
      isReenterable: true,
      isGreedy: false,
    },
    children: [],
  });
  const originalCurationData = useRef<IHomeCuration>({
    featuredTitle: '',
    homeCurations: [],
  });
  const canceler = useRef<Canceler>(() => undefined);

  // states
  const [submenu, setSubmenu] = useState<'setting' | 'curation' | string>((pathMatch && pathMatch[2]) || 'setting');
  const [homeType, setHomeType] = useState<HomeType>();
  const [curationMode, setCurationMode] = useState<'create' | 'edit' | 'view' | string>((pathMatch && pathMatch[3]) || 'view');
  const [segment, setSegment] = useState<IUserSegment>();
  const [isEditing, setIsEditing] = useState<boolean>(!segmentId || curationMode !== 'view');
  const [segmentName, setSegmentName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [segmentType, setSegmentType] = useState<UserSegmentType>(UserSegmentType.AutoUpdate);
  const [triggerType, setTriggerType] = useState<TriggerType>(TriggerType.OnboardingFlow);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [sql, setSql] = useState<string>('');
  const [pendingTest, setPendingTest] = useState<boolean>(!segmentId ? false : true);
  const [testNumberOfUsers, setTestNumberOfUsers] = useState<number>(0);
  const [testResultStatus, setTestResultStatus] = useState<number | undefined>(!segmentId ? undefined : 201);
  const [intervalType, setIntervalType] = useState<IntervalType>(IntervalType.Daily);
  const [intervalValue, setIntervalValue] = useState<number>();
  const [period, setPeriod] = useState<number>();
  const [runImmediately, setRunImmediately] = useState<boolean>(false);
  const [nextFireDescriptions, setNextFireDescriptions] = useState<string[]>();
  const [maxAttendanceDuration, setMaxAttendanceDuration] = useState<string>('');
  const [persistentType, setPersistentType] = useState<UserSegmentPersistentType>(UserSegmentPersistentType.Long);
  const [isReenterable, setIsReenterable] = useState<boolean>(true);
  const [isGreedy, setIsGreedy] = useState<boolean>(false);
  const [currentSegmentId, setCurrentSegmentId] = useState<number>();
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [nextAt, setNextAt] = useState<string | undefined>();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [pendingInitialLoading, setPendingInitialLoading] = useState<boolean>(false);
  const [distinctFlowKeys, setDistinctFlowKeys] = useState<string[]>([]);
  const [brazeTriggerType, setBrazeTriggerType] = useState<UserSegmentBrazeTriggerType>(UserSegmentBrazeTriggerType.None);
  const [brazeTriggerId, setBrazeTriggerId] = useState<string>('');
  const [brazeTriggerWhenReentering, setBrazeTriggerWhenReentering] = useState<boolean>(false);
  const [childSegments, setChildSegments] = useState<IUserSegment[]>([]);
  const [featuredTitle, setFeaturedTitle] = useState<string>('');
  const [curationFeaturedCards, setCurationFeaturedCards] = useState<ISegmentCuration[]>([]);
  const [curationCollections, setCurationCollections] = useState<ISegmentCuration[]>([]);
  const [featuredCards, setFeaturedCards] = useState<ISegmentCurationFeaturedCard[]>([]);
  const [collections, setCollections] = useState<ISegmentCurationCollection[]>([]);
  const [isSystemSegment, setIsSystemSegment] = useState<boolean>(false);

  const [excludedCurrentHomeData, setExcludedCurrentHomeData] = useState<ISegmentCuration[]>([]);

  const setStatesFromCurationData = useCallback(
    (sourceType: 'fetch' | 'component', data: IHomeCuration) => {
      if (homeType === undefined) return;

      setFeaturedTitle(data.featuredTitle);

      const filters = getHomeTypeFilters(homeType);
      const featuredCards = data.homeCurations.filter(i => i.type === filters.FeaturedCardsFilter) as ISegmentCurationFeaturedCard[];
      const collections = data.homeCurations.filter(i => i.type === filters.CollectionsFilter) as ISegmentCurationCollection[];

      setExcludedCurrentHomeData(
        data.homeCurations.filter(curation => curation.type !== filters.FeaturedCardsFilter && curation.type !== filters.CollectionsFilter),
      );

      if (sourceType === 'component') {
        setCurationFeaturedCards(featuredCards);
        setCurationCollections(collections);
      } else {
        setFeaturedCards(featuredCards);
        setCollections(collections);
      }
    },
    [homeType],
  );
  const setStatesFromData = (data: IUserSegment) => {
    setSegment(data);
    setSegmentName(data.name);
    setDescription(data.description);
    setSegmentType(data.type);
    setMaxAttendanceDuration(data.daysToExpiration || '');

    if (data.type === UserSegmentType.AutoUpdate && data.autoupdateOption !== undefined) {
      setSql(data.autoupdateOption.sql);
      setIntervalType(data.autoupdateOption.intervalType);
      setIntervalValue(data.autoupdateOption.intervalValue);

      setPeriod(data.autoupdateOption.period);

      setPersistentType(data.autoupdateOption.persistentType);
      setIsReenterable(data.autoupdateOption.isReenterable);
      setIsGreedy(data.autoupdateOption.isGreedy);

      if (data.autoupdateOption.nextAt !== undefined) {
        setNextAt(data.autoupdateOption.nextAt);
      }

      if (data.autoupdateOption.isActive !== undefined) {
        setIsActive(data.autoupdateOption.isActive);
      }
    }

    if (data.type === UserSegmentType.Trigger && data.triggerOption !== undefined) {
      setTriggerType(data.triggerOption.trigger);
      setPersistentType(data.triggerOption.persistentType);
      setIsReenterable(data.triggerOption.isReenterable);
      setIsGreedy(data.triggerOption.isGreedy);
      setSelectedProperties(data.triggerOption.properties);
    }

    if (data.type === UserSegmentType.Webhook && data.webhookOption !== undefined) {
      setPersistentType(data.webhookOption.persistentType);
      setIsReenterable(data.webhookOption.isReenterable);
      setIsGreedy(data.webhookOption.isGreedy);
    }

    if (data.brazeTriggerType !== undefined) {
      setBrazeTriggerType(data.brazeTriggerType);
      setBrazeTriggerId(data.brazeTriggerId!);
      setBrazeTriggerWhenReentering(data.brazeTriggerWhenReentering!);
    }

    if (data.type === UserSegmentType.Group && data.children !== undefined) {
      setChildSegments(data.children);
    }

    if (data.type === UserSegmentType.System || data.id === 45 || data.type === UserSegmentType.Product) {
      setIsSystemSegment(true);
    } else {
      setIsSystemSegment(false);
    }

    setPendingTest(false);
    setTestResultStatus(201);
    setRunImmediately(false);

    if (data.id !== undefined) {
      setCurrentSegmentId(data.id);
    }
  };

  // Use to determine repetition cycle.
  const IntervalValueMap = useMemo((): Record<string, number[]> => {
    return {
      hour: Array.from({ length: 23 }, (v, k) => k + 1),
      day: Array.from({ length: 29 }, (v, k) => k + 1),
      week: Array.from({ length: 5 }, (v, k) => k + 1),
      month: Array.from({ length: 12 }, (v, k) => k + 1),
    };
  }, []);

  const IntervalPeriodMap = useMemo((): Record<string, number[]> => {
    return {
      hour: [],
      day: Array.from({ length: 24 }, (v, k) => k), // 0 ~ 23
      week: Array.from({ length: 7 }, (v, k) => k), // Sun ~ Sat
      month: Array.from({ length: 30 }, (v, k) => k + 1), // 1st ~ 31st
    };
  }, []);

  const fetchDistinctFlowKeys = async () => {
    const flowKeysResult = await getDistinctFlowKeys();

    setDistinctFlowKeys(flowKeysResult.data.map(p => p.key));
  };

  // submit validation
  useEffect(() => {
    const messageSet = new Set<string>();

    if (!segmentName) {
      messageSet.add('A title cannot be empty.');
    }

    if (!description) {
      messageSet.add('A description cannot be empty.');
    }

    if (segmentType === UserSegmentType.Group) {
      if (childSegments.length < 2) {
        messageSet.add('Add 2 or more segments.');
      }
    } else if (segmentType !== UserSegmentType.System) {
      if (!maxAttendanceDuration) {
        messageSet.add('A maximum attendance duration cannot be empty.');
      } else if (parseInt(maxAttendanceDuration) <= 0) {
        messageSet.add('A maximum attendance duration must be greater than 0.');
      }
    }

    if (segmentType === UserSegmentType.AutoUpdate) {
      if (!intervalType) {
        messageSet.add('An interval type cannot be empty.');
      }

      if (!intervalValue) {
        messageSet.add('An interval value cannot be empty.');
      }

      if (intervalType !== 'hour' && period === undefined) {
        messageSet.add('A period cannot be empty.');
      }

      if (!sql) {
        messageSet.add('A sql cannot be empty.');
      }

      if (testResultStatus !== 201) {
        messageSet.add('A SQL statement is should be passed the test.');
      }
    }

    if (segmentType === UserSegmentType.Trigger && triggerType === TriggerType.OnboardingFlow) {
      if (!selectedProperties || selectedProperties.length === 0) {
        messageSet.add('At least one flow key must be selected.');
      }
    }

    if (brazeTriggerType === UserSegmentBrazeTriggerType.None) {
      setBrazeTriggerId('');
      setBrazeTriggerWhenReentering(false);
    } else {
      if (!brazeTriggerId) {
        messageSet.add(`${brazeTriggerType === UserSegmentBrazeTriggerType.Campaign ? 'Campaign' : 'Canvas'} ID must not be empty.`);
      }
    }

    if (segmentId) {
      // if (featuredTitle === '') {
      //   messageSet.add('Curation featured title cannot be empty.');
      // }
      // if (curationFeaturedCards.length <= 1) {
      //   messageSet.add('Curation featured cards more than 2');
      // }
      // if (curationCollections.length <= 1) {
      //   messageSet.add('Curation collections more than 2');
      // }
    }

    const messages = Array.from(messageSet);

    setValidationMessages(messages);
  }, [
    segmentName,
    description,
    intervalType,
    intervalValue,
    period,
    sql,
    testResultStatus,
    maxAttendanceDuration,
    segmentType,
    triggerType,
    selectedProperties,
    brazeTriggerId,
    brazeTriggerType,
    brazeTriggerWhenReentering,
    childSegments,
    featuredTitle,
    curationFeaturedCards,
    curationCollections,
  ]);

  // APIs

  const fetchCurationData = useCallback(async () => {
    if (!segmentId) {
      return;
    }
    const [fetchPromise2, cancel2] = getUserSegmentCuration(segmentId);
    const resultCuration = await fetchPromise2;
    if (resultCuration.status === 200) {
      originalCurationData.current = resultCuration.data;
      setStatesFromCurationData('fetch', originalCurationData.current);

      if (homeType === undefined) {
        const getMaximumType = flow(homeTypeCounter, getMaximum);
        const maxType = getMaximumType(originalCurationData.current);
        const initialHomeType = (maxType as unknown) as HomeType;

        setHomeType(initialHomeType);
      }
    }
  }, [segmentId, setStatesFromCurationData]);

  const fetchData = useCallback(async () => {
    if (!segmentId) {
      return;
    }

    canceler.current();

    try {
      setPendingInitialLoading(true);
      const [fetchPromise, cancel] = getUserSegment(segmentId);
      canceler.current = cancel;
      const result = await fetchPromise;

      if (result.status === 200) {
        await fetchCurationData();
        originalData.current = result.data;
        setStatesFromData(result.data);
      }
    } catch (e) {
      if (api.isCancel(e)) return;

      if (onError) {
        onError(e);
      }
    } finally {
      setPendingInitialLoading(false);
    }
  }, [segmentId, fetchCurationData, onError]);

  // Watches
  useEffect(() => {
    fetchData();

    return () => canceler.current();
  }, [fetchData]);

  // useEffect(() => {
  //   if (homeType !== undefined) {
  //     return;
  //   }

  //   const getMaximumType = flow(homeTypeCounter, getMaximum);
  //   const maxType = getMaximumType(originalCurationData.current);
  //   const initialHomeType = (maxType as unknown) as HomeType;

  //   setHomeType(initialHomeType);

  //   console.log(`homeTypeChanged - setStatesFromCurationData`);
  // }, [homeType]);

  useEffect(() => {
    if (!intervalValue) {
      setNextFireDescriptions([]);
      return;
    }

    if (intervalType !== 'hour' && period === undefined) {
      setNextFireDescriptions([]);
      return;
    }

    const nextAts = [];
    const nextFireDate = getNextFireDate(intervalType, intervalValue, period, undefined, nextAt);

    if (nextFireDate) {
      for (let i = 0; i < 5; i++) {
        nextAts.push(nextFireDate.format('LLLL z'));
        nextFireDate.add(intervalValue, intervalType as DurationInputArg2);
      }
    }

    if (runImmediately) {
      nextAts.unshift(moment().format('LLLL z'));
    } else if (nextAt) {
      const formattedNextAt = moment(nextAt).format('LLLL z');

      if (formattedNextAt !== nextAts[0]) {
        nextAts.unshift(formattedNextAt);
      }
    }

    setNextFireDescriptions(nextAts.slice(0, 5));
  }, [intervalValue, period, runImmediately, nextAt, intervalType]);

  useEffect(() => {
    if (segmentType === UserSegmentType.Trigger && triggerType === TriggerType.OnboardingFlow) {
      const fetchData = async () => {
        fetchDistinctFlowKeys();
      };

      fetchData();
    }
  }, [segmentType, triggerType]);

  // Handlers

  const handleClickSubmit = useCallback(async () => {
    const stateToPayload = (): IUserSegment => {
      const payload: IUserSegment = {
        name: segmentName,
        type: segmentType,
        description,
        daysToExpiration: maxAttendanceDuration,
      };
      payload.brazeTriggerType = brazeTriggerType;
      payload.brazeTriggerId = brazeTriggerId;
      payload.brazeTriggerWhenReentering = brazeTriggerWhenReentering;

      if (payload.type === UserSegmentType.Group) {
        payload.childSegmentIds = childSegments.map(item => item.id!);
      } else {
        payload.dte = String(payload.daysToExpiration);
      }

      if (payload.type === UserSegmentType.AutoUpdate) {
        let calculatedPeriod = period;

        if (intervalType === IntervalType.Daily) {
          // Local time zone to UTC (server)
          calculatedPeriod = Number(
            moment(period, 'H')
              .utc()
              .format('H'),
          );
        }

        return {
          ...payload,
          autoupdateOption: {
            sql,
            intervalType,
            intervalValue,
            period: calculatedPeriod,
            persistentType,
            isReenterable,
            isGreedy,
            immediately: runImmediately,
          } as IUserSegmentAutoUpdateOption,
        } as IUserSegmentAutoUpdate;
      }

      if (payload.type === UserSegmentType.Trigger) {
        return {
          ...payload,
          triggerOption: {
            trigger: triggerType,
            properties: selectedProperties,
            isActive: true,
            persistentType,
            isReenterable,
            isGreedy,
          },
        } as IUserSegmentTrigger;
      }

      if (payload.type === UserSegmentType.Webhook) {
        return {
          ...payload,
          webhookOption: {
            persistentType,
            isReenterable,
            isGreedy,
            isActive: true,
          },
        } as IUserSegmentWebhook;
      }

      return payload as IUserSegment;
    };
    const stateToCurationPayload = (): IHomeCuration => {
      const payload: IHomeCuration = {
        featuredTitle: featuredTitle,
        homeCurations: [...excludedCurrentHomeData, ...curationFeaturedCards, ...curationCollections].map(item => {
          return {
            id: item.id,
            segmentId: currentSegmentId,
            type: item.type,
            priority: item.priority,
            startsAt: item.startsAt,
            endsAt: item.endsAt,
            targetId: item.targetId,
          };
        }) as ISegmentCuration[],
      };
      return payload;
    };

    try {
      backdrop.show();
      const payload: IUserSegment = stateToPayload();

      if (currentSegmentId) {
        // update
        let settingSuccess = isSystemSegment ? true : false;
        let result: AxiosResponse | undefined = undefined;
        if (!isSystemSegment) {
          result = await editUserSegment(currentSegmentId, payload);
          settingSuccess = api.isResponseSuccess(result);
        }
        if (settingSuccess) {
          const curationResult = await saveUserSegmentCuration(currentSegmentId, stateToCurationPayload());
          setIsEditing(false);
          fetchData();
          if (result && !isSystemSegment && onCreated) {
            onCreated(result?.data as IUserSegmentTrigger | IUserSegmentAutoUpdate);
          }
        }
      } else {
        // create
        const result = await saveUserSegment(payload);
        if (originalData.current) {
          setStatesFromData(originalData.current);
          setChildSegments([]);
        }
        if (onCreated) {
          onCreated(result.data as IUserSegmentTrigger | IUserSegmentAutoUpdate);
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
  }, [
    fetchData,
    backdrop,
    currentSegmentId,
    isEditing,
    onCreated,
    onError,
    description,
    intervalType,
    intervalValue,
    isGreedy,
    isReenterable,
    maxAttendanceDuration,
    period,
    persistentType,
    runImmediately,
    segmentName,
    segmentType,
    selectedProperties,
    sql,
    triggerType,
    brazeTriggerType,
    brazeTriggerId,
    brazeTriggerWhenReentering,
    childSegments,
    featuredTitle,
    curationFeaturedCards,
    curationCollections,
  ]);

  const isReadOnly = isEditing === false && !!currentSegmentId;

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
    setCurationMode('edit');
  }, []);

  const handleCancelClick = useCallback(() => {
    if (originalData.current) {
      setStatesFromData(originalData.current);
    }
    if (originalCurationData.current) {
      setStatesFromCurationData('component', originalCurationData.current);
    }

    if (isEditing) {
      setIsEditing(false);
      setCurationMode('view');
    }
  }, [isEditing]);

  const handleTitleChange = useCallback(
    e => {
      if (isReadOnly) return;
      setSegmentName(e.target.value);
    },
    [isReadOnly],
  );

  const handleDescriptionChange = useCallback(
    e => {
      if (isReadOnly) return;
      setDescription(e.target.value);
    },
    [isReadOnly],
  );

  const handleTypeChange = useCallback(e => {
    if (e.target.value) {
      setSegmentType(String(e.target.value) as UserSegmentType);
    }
  }, []);

  const handleTriggerTypeChange = useCallback(e => setTriggerType(String(e.target.value) as TriggerType), []);

  const flowKeyRenderer = useCallback(
    selected => (
      <div style={{ margin: 2 }}>
        {(selected as string[]).map(value => (
          <Chip key={value} label={value} style={{ margin: 2 }} />
        ))}
      </div>
    ),
    [],
  );

  const handleSelectedFlowKeyChange = useCallback(e => {
    setSelectedProperties(e.target.value as string[]);
  }, []);

  const handleQueryChange = useCallback(
    e => {
      if (isReadOnly) return;

      setSql(e.target.value);
      setTestResultStatus(undefined);
    },
    [isReadOnly],
  );

  const handleTestClick = useCallback(async () => {
    setPendingTest(true);

    try {
      const result = await getAutoUpdateUserSegmentQueryResult(sql);

      if (result.status === 201) {
        const { numberOfUsers } = result.data;
        // Test passed
        setTestNumberOfUsers(numberOfUsers);
      }

      setTestResultStatus(result.status);
      setPendingTest(false);
    } catch (e) {
      console.error(e);

      if (onError) {
        onError(e);
      }

      setTestResultStatus(500);
      setPendingTest(false);
    }
  }, [onError, sql]);

  const handleIntervalTypeChange = useCallback(e => {
    setIntervalType(e.target.value as IntervalType);

    setIntervalValue(undefined);
    setPeriod(undefined);
    setNextAt(undefined);
    setNextFireDescriptions([]);
  }, []);

  const handleIntervalValueChange = useCallback(e => {
    setIntervalValue(e.target.value as number);

    setPeriod(undefined);
    setNextAt(undefined);
    setNextFireDescriptions([]);
  }, []);

  const handlePeriodChange = useCallback(e => {
    setPeriod(Number(e.target.value));
    setNextAt(undefined);
  }, []);

  const handleRunImmediatelyChange = useCallback(() => {
    setNextAt(undefined);
    setRunImmediately(ps => !ps);
  }, []);

  const handleMaxAttendanceDurationChange = useCallback(e => setMaxAttendanceDuration(e.target.value), []);

  const handlePersistentTypeChange = useCallback(e => setPersistentType(e.target.value as UserSegmentPersistentType), []);

  const handleIsReenterableChange = useCallback(e => setIsReenterable(e.target.value === 'true'), []);

  const handleIsGreedyChange = useCallback(e => setIsGreedy(e.target.value === 'true'), []);

  const handleOnchangeBrazeTriggerType = useCallback(e => setBrazeTriggerType(e.target.value), []);
  const handleOnchangeBrazeTriggerId = useCallback(e => setBrazeTriggerId(e.target.value), []);
  const handleOnchangeBrazeTriggerWhenReentering = useCallback(e => setBrazeTriggerWhenReentering(e.target.value === 'true'), []);

  const handleOnChangeCuration = useCallback((segmentCuration: IHomeCuration) => {
    setStatesFromCurationData('component', segmentCuration);
  }, []);
  const handleOnChangeFeaturedTitle = useCallback(
    (fTitle: string) => {
      setFeaturedTitle(fTitle);
    },
    [featuredTitle],
  );

  const handleOnChangeFeaturedCards = useCallback(
    (fCards: ISegmentCuration[]) => {
      setCurationFeaturedCards(fCards);
    },
    [curationFeaturedCards],
  );

  const handleOnChangeCollections = useCallback(
    (clections: ISegmentCuration[]) => {
      setCurationCollections(clections);
    },
    [curationCollections],
  );

  // segment list
  const [segmentList, setSegmentList] = useState<IUserSegment[]>([]);
  const [isSearchingSegments, setIsSearchingSegments] = useState<boolean>(false);
  const selectedSegmentIds: number[] = useMemo(() => childSegments.map(item => item.id!), [childSegments]);
  const handleDeleteSegmentFromGroup = useCallback(
    (idxDelete: number) => {
      setChildSegments(
        childSegments.filter((item, idx) => {
          return idx !== idxDelete;
        }),
      );
    },
    [childSegments],
  );

  const handleAddSegmentToList = useCallback(
    (item: IUserSegment) => {
      setChildSegments([...childSegments, item]);
    },
    [childSegments],
  );

  const fetchSegmentList = async (keyword = '') => {
    setIsSearchingSegments(true);
    try {
      const result = await searchUserSegments(
        [UserSegmentType.AutoUpdate, UserSegmentType.System, UserSegmentType.Trigger, UserSegmentType.Webhook],
        keyword,
        true,
      );
      setSegmentList(result.data.items.filter(item => item.type !== UserSegmentType.Group && item.type !== UserSegmentType.System && item.id !== 45));
    } catch (e) {
      snackbar.error(e);
    } finally {
      setIsSearchingSegments(false);
    }
  };

  const handleOnChangeHomeType = (_: any, _homeType: HomeType) => setHomeType(_homeType);
  // init
  useEffect(() => {
    if (segmentType === UserSegmentType.Group && isEditing && segmentId === undefined) {
      fetchSegmentList();
    }
  }, [isEditing, segmentType]);

  useEffect(() => {
    if (segmentId && location.pathname.match(`/v/${segmentId}`)) {
      setSubmenu((pathMatch && pathMatch[2]) || 'setting');
      setCurationMode((pathMatch && pathMatch[3]) || 'view');
      setIsEditing(!segmentId || curationMode !== 'view');
    }
  }, [location.pathname]);

  if (pendingInitialLoading) {
    return <div />;
  }

  return (
    <div className={classes.root}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '8px', marginBottom: '14px', paddingLeft: '4px' }}
      >
        <Typography variant="h6" component="h3">
          {!currentSegmentId ? 'Create Segment' : `Edit ${originalData.current?.name} (${originalData.current?.id})`}
        </Typography>
        <SubmitControl
          validationMessages={validationMessages}
          className={classes.bottomButtons}
          mode={!currentSegmentId ? 'create' : isEditing ? 'edit' : 'view'}
          onSubmitClick={handleClickSubmit}
          onEditClick={handleEditClick}
          onCancelClick={handleCancelClick}
        />
      </Box>
      {segmentId && (
        <Box marginBottom={2}>
          <Tabs value={submenu}>
            <Tab
              label="Segment Setting"
              value="setting"
              onClick={() => {
                setSubmenu('setting');
              }}
            />
            <Tab
              label="Curation"
              value="curation"
              onClick={() => {
                if (submenu !== 'curation') {
                  // history.push(`${location.pathname}/curation`);
                  setSubmenu('curation');
                }
              }}
            />
          </Tabs>
        </Box>
      )}
      <Box display={submenu === 'setting' ? 'block' : 'none'}>
        <Paper className={classes.paper}>
          <Grid container>
            <Grid item xs={12}>
              <Grid container className={classes.row}>
                <Grid item xs={3}>
                  <Typography variant="subtitle1">Segment Title</Typography>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    placeholder="Segment Title"
                    fullWidth
                    value={segmentName}
                    disabled={isSystemSegment || isReadOnly}
                    onChange={handleTitleChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container className={classes.row}>
                <Grid item xs={3}>
                  <Typography variant="subtitle1">Description</Typography>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    placeholder="Description"
                    fullWidth
                    value={description}
                    disabled={isSystemSegment || isReadOnly}
                    onChange={handleDescriptionChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container className={classes.row}>
                <Grid item xs={3}>
                  <Typography variant="subtitle1">Type</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Select fullWidth value={segmentType} disabled={!!currentSegmentId} onChange={handleTypeChange}>
                    {segmentId && (
                      <MenuItem value={UserSegmentType.Product} disabled={true}>
                        Product
                      </MenuItem>
                    )}
                    {segmentId && (
                      <MenuItem value={UserSegmentType.Survey} disabled={true}>
                        Survey
                      </MenuItem>
                    )}
                    <MenuItem value={UserSegmentType.AutoUpdate}>Auto-Update</MenuItem>
                    <MenuItem value={UserSegmentType.Trigger}>Trigger</MenuItem>
                    <MenuItem value={UserSegmentType.Webhook}>Webhook</MenuItem>
                    {segmentType === UserSegmentType.System && <MenuItem value={UserSegmentType.System}>System</MenuItem>}
                    <MenuItem value={''}>----------------------</MenuItem>
                    <MenuItem value={UserSegmentType.Group}>Group segment</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </Grid>
            {segmentType === UserSegmentType.Trigger && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Trigger</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Select
                      fullWidth
                      value={triggerType}
                      disabled={!!currentSegmentId || triggerType === TriggerType.JoinedRadish}
                      onChange={handleTriggerTypeChange}
                    >
                      <MenuItem value={TriggerType.OnboardingFlow}>Onboarding Flow</MenuItem>
                      <MenuItem value={TriggerType.ReferralCode}>Referral Code</MenuItem>
                      <MenuItem value={TriggerType.RedeemCode}>Story Redeem Code</MenuItem>
                      <MenuItem value={TriggerType.SegmentAttendanceExpired} disabled={true}>
                        Segment Attendance Expired
                      </MenuItem>
                      <MenuItem value={TriggerType.JoinedRadish} disabled={true}>
                        Joined Radish
                      </MenuItem>
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {segmentType === UserSegmentType.Trigger && triggerType == TriggerType.OnboardingFlow && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Flow Key</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <FormControl fullWidth>
                      <Select
                        classes={{
                          selectMenu: classes.flowKeySelectMenu,
                        }}
                        fullWidth
                        multiple
                        value={selectedProperties}
                        renderValue={flowKeyRenderer}
                        disabled={isReadOnly || distinctFlowKeys.length <= 0}
                        onChange={handleSelectedFlowKeyChange}
                      >
                        {distinctFlowKeys.map(key => {
                          return (
                            <MenuItem value={key} key={key}>
                              {key}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {segmentType === UserSegmentType.Trigger && (triggerType === TriggerType.ReferralCode || triggerType === TriggerType.RedeemCode) && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Code</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <FormTextField
                      fullWidth
                      defaultValue={selectedProperties[0]}
                      disabled={isReadOnly}
                      onValueSubmit={(code: string) => setSelectedProperties([code])}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {segmentType === UserSegmentType.AutoUpdate && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3} className={classes.topAlignColumn}>
                    <Typography variant="subtitle1">SQL</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Grid container>
                      <Grid item xs={9}>
                        <TextareaAutosize rows={8} className={classes.queryTextArea} value={sql} onChange={handleQueryChange} />
                      </Grid>

                      <Grid item xs={3}>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.testButton}
                          disabled={sql.length <= 0 || pendingTest || isReadOnly}
                          onClick={handleTestClick}
                        >
                          TEST
                          {pendingTest && <CircularProgress size={24} className={classes.testButtonProgress} />}
                        </Button>
                        {testResultStatus === 201 && <DoneIcon style={{ marginLeft: '36px', marginTop: '6px', color: green['A700'] }} />}
                        {testResultStatus !== 201 && testResultStatus !== undefined && (
                          <ClearIcon style={{ marginLeft: '36px', marginTop: '6px', color: red['A700'] }} />
                        )}
                      </Grid>
                    </Grid>

                    {testResultStatus !== 201 && (
                      <Typography variant="caption" className={classes.testResultDescriptionError}>
                        A SQL statement must be tested prior to saving the segment.
                      </Typography>
                    )}
                    {testResultStatus === 201 && testNumberOfUsers > 0 && (
                      <Typography variant="caption">{`Result: ${testNumberOfUsers?.toLocaleString()} users are in this segment now.`}</Typography>
                    )}
                    {testResultStatus === 201 && testNumberOfUsers === 0 && (
                      <Typography variant="caption">There is no result at this time. 😕</Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
            {segmentType === UserSegmentType.AutoUpdate && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3} className={classes.topAlignColumn}>
                    <Typography variant="subtitle1">Refresh Interval</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <FormControl className={classes.formControl}>
                      <InputLabel shrink htmlFor="interval-type">
                        Interval Type
                      </InputLabel>
                      <Select fullWidth value={intervalType} disabled={isReadOnly} onChange={handleIntervalTypeChange}>
                        {process.env.ENV !== 'production' && <MenuItem value={IntervalType.Hourly}>Hourly (Dev only)</MenuItem>}
                        <MenuItem value={IntervalType.Daily}>Daily</MenuItem>
                        <MenuItem value={IntervalType.Weekly}>Weekly</MenuItem>
                        <MenuItem value={IntervalType.Monthly}>Monthly</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl className={classes.formControl}>
                      <InputLabel shrink htmlFor="interval-value">
                        Interval Value
                      </InputLabel>
                      <Select
                        value={intervalValue || ''}
                        onChange={handleIntervalValueChange}
                        input={<Input name="intervalValue" id="interval-value" />}
                        name="intervalValue"
                        displayEmpty
                        disabled={isReadOnly}
                      >
                        {IntervalValueMap[intervalType].map((v, i) => (
                          // WARNING: JUST FOR QA TEST CONVENIENCE
                          <MenuItem value={v} key={`i-v-${intervalType}-${i}`}>
                            {v === 1 ? `Every ${intervalType}` : `Every ${v} ${intervalType}s`}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Determine the repeat cycle.</FormHelperText>
                    </FormControl>

                    <FormControl className={classes.formControl}>
                      <InputLabel shrink htmlFor="period">
                        Period
                      </InputLabel>
                      <Select
                        value={
                          intervalType === IntervalType.Hourly || period === undefined
                            ? ''
                            : intervalType === IntervalType.Monthly || intervalType === IntervalType.Daily
                            ? period
                            : convertPeriodWithTimezone(intervalType, period)
                        }
                        onChange={handlePeriodChange}
                        input={<Input name="period" id="period" />}
                        name="period"
                        displayEmpty
                        disabled={intervalType === IntervalType.Hourly || isReadOnly}
                      >
                        {IntervalPeriodMap[intervalType].map((v, i) => {
                          if (intervalType === IntervalType.Hourly) {
                            return;
                          }

                          if (intervalType === IntervalType.Daily) {
                            return (
                              <MenuItem value={v} key={`i-v-p-${intervalType}-${i}`}>
                                {moment(v, 'H').format('HH:mm')}
                              </MenuItem>
                            );
                          }

                          if (intervalType === IntervalType.Weekly) {
                            return (
                              <MenuItem value={v} key={`i-v-p-${intervalType}-${i}`}>
                                {moment(v, 'e').format('dddd')}
                              </MenuItem>
                            );
                          }

                          if (intervalType === IntervalType.Monthly) {
                            return (
                              <MenuItem value={v - 1} key={`i-v-p-${intervalType}-${i}`}>
                                {moment(v, 'DD').format('Do')}
                              </MenuItem>
                            );
                          }
                        })}
                      </Select>
                    </FormControl>

                    {!isReadOnly && (
                      <FormControlLabel
                        control={<Checkbox checked={runImmediately} onChange={handleRunImmediatelyChange} disabled={isReadOnly} />}
                        label="Run Query Immediately"
                        disabled={isReadOnly}
                      />
                    )}

                    <Box className={classes.nextSegmentInfoBox}>
                      {(runImmediately || isActive) && nextFireDescriptions && nextFireDescriptions.length > 0 && (
                        <Fragment>
                          <Typography variant="caption" display="block" key={'title'} className={classes.nextFireTitle}>
                            Time to update the next five segments:
                          </Typography>
                          {nextFireDescriptions?.map((text, i) => (
                            <Typography variant="caption" key={`nfd-${i}`} display="block" color={i === 0 ? 'secondary' : 'textSecondary'}>
                              {text}
                            </Typography>
                          ))}
                        </Fragment>
                      )}

                      {!runImmediately && !isActive && (
                        <Typography variant="caption" display="block" key={'title'} className={classes.nextFireTitle}>
                          When a status is active, You can find out when to create your next segments.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {segmentType === UserSegmentType.Group && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Segments</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Grid container className={classes.dualTableWrap}>
                      <Grid item className={isEditing && !segmentId ? classes.dualTableLeftWrap : classes.dualTableLeftWrapExpand}>
                        <Table className={classes.thinTable}>
                          <TableHead>
                            <TableRow className={classes.dualTableLeftHeader}>
                              <TableCell width="70" align="center">
                                Segment ID
                              </TableCell>
                              <TableCell>Segment Title</TableCell>
                              <TableCell width="70" align="center">
                                User Count
                              </TableCell>
                              <TableCell width="36" align="center">
                                Delete
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {childSegments &&
                              childSegments.map((segmentItem: IUserSegment, idx: number) => (
                                <TableRow className={''} key={`segment-list-item-${segmentItem.id!}-${idx}`}>
                                  <TableCell align="center">{segmentItem.id!}</TableCell>
                                  <TableCell>{segmentItem.name}</TableCell>
                                  <TableCell align="center">{segmentItem.userCount || 0}</TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      disabled={!isEditing || segmentId !== undefined}
                                      onClick={() => {
                                        handleDeleteSegmentFromGroup(idx);
                                      }}
                                    >
                                      <DeleteIcon color={isEditing && segmentId === undefined ? 'primary' : 'disabled'} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </Grid>
                      {isEditing && !segmentId && (
                        <Grid item className={classes.dualTableRightWrap} style={{ maxWidth: 454 }}>
                          <Box className={classes.dualTableRightBox}>
                            <Box className={classes.dualTableRightHeader}>
                              <Typography component="span" className={classes.dualTableRightHeaderText}>
                                Add Segments
                              </Typography>
                            </Box>
                            <Box className={classes.dualTableRightContent} padding={1}>
                              <Box paddingX="16px">
                                <SearchTextField
                                  placeholder="Enter Segment Title or Segment ID"
                                  isSearching={isSearchingSegments}
                                  fullWidth
                                  onCancel={() => fetchSegmentList('')}
                                  onValueSubmit={v => {
                                    fetchSegmentList(v);
                                  }}
                                />
                              </Box>
                              <Box className={classes.listBox}>
                                <List classes={{ root: classes.listWrap }}>
                                  {segmentList &&
                                    segmentList.map((segmentItem, idx) => {
                                      const unselectable = selectedSegmentIds.indexOf(segmentItem.id!) >= 0;

                                      return (
                                        <ListItem
                                          key={`seg-group-item-${segmentItem.id}-${idx}`}
                                          dense={true}
                                          disableGutters={true}
                                          classes={{ root: classes.displayFeaturedItem }}
                                        >
                                          <Box display="flex" flexDirection="column" className={classes.displayTitleWrap}>
                                            <Typography
                                              className={classes.displayTitle}
                                              color={false ? 'textSecondary' : 'textPrimary'}
                                              variant="body2"
                                            >
                                              {segmentItem.name}
                                            </Typography>
                                          </Box>

                                          <IconButton
                                            className={classes.displayAddButton}
                                            size="medium"
                                            onClick={() => {
                                              handleAddSegmentToList(segmentItem);
                                            }}
                                            disabled={unselectable}
                                          >
                                            <AddBoxIcon color={unselectable ? 'disabled' : 'primary'} />
                                          </IconButton>
                                        </ListItem>
                                      );
                                    })}
                                </List>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {segmentType !== UserSegmentType.Group && segmentType !== UserSegmentType.System && (
              <Grid item xs={12}>
                <Grid container className={classes.row}>
                  <Grid item xs={3} className={classes.topAlignColumn}>
                    <Typography variant="subtitle1">
                      Attendance
                      <br />
                      Settings
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Grid container direction="column">
                      <Grid item>
                        <Typography variant="body1" className={classes.maxAttendanceText} component="div">
                          <Box>
                            <span>Maximum Attendance Duration</span>
                            <Help
                              title="Maximum Attendance Duration"
                              text="The maximum duration a user can be in a particular segment. 
                          A user is removed from a segment once he/she reaches this maximum duration."
                            />
                          </Box>
                          <TextField
                            className={classes.smallTextField}
                            variant="outlined"
                            margin="dense"
                            value={maxAttendanceDuration}
                            disabled={isReadOnly}
                            onChange={handleMaxAttendanceDurationChange}
                          />
                          Days
                        </Typography>
                      </Grid>
                      {(segmentType === UserSegmentType.AutoUpdate ||
                        segmentType === UserSegmentType.Webhook ||
                        segmentType === UserSegmentType.Trigger) && (
                        <Grid item>
                          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" component="div">
                              <Box>
                                <span>Persistent Type</span>
                                <Help
                                  title="Persistent Type"
                                  text="When a user makes it into a segment, short-living segments count its expiration date from the user’s earliest history, thus the expiration date doesn’t change.
long-living segments count its expiration date from the user’s latest history, thus its expiration date is renewed every time. "
                                />
                              </Box>
                            </Typography>

                            <RadioGroup
                              name="persistentType"
                              value={persistentType}
                              onChange={handlePersistentTypeChange}
                              className={classes.radioButtonGroups}
                            >
                              <FormControlLabel value={'long'} control={<Radio color="primary" disabled={isReadOnly} />} label="Long-living" />
                              <FormControlLabel value={'short'} control={<Radio color="primary" disabled={isReadOnly} />} label="Short-living" />
                            </RadioGroup>
                          </Box>
                        </Grid>
                      )}
                      {(segmentType === UserSegmentType.AutoUpdate ||
                        segmentType === UserSegmentType.Webhook ||
                        segmentType === UserSegmentType.Trigger) && (
                        <Grid item>
                          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" component="div">
                              <Box>
                                <span>Eligibility for reentering segment</span>
                                <Help
                                  title="Eligibility for reentering segment"
                                  text="Is a user eligible to re-enter the segment after being removed from the segment upon expiration?"
                                />
                              </Box>
                            </Typography>
                            <RadioGroup
                              name="reenterable"
                              value={isReenterable}
                              onChange={handleIsReenterableChange}
                              className={classes.radioButtonGroups}
                            >
                              <FormControlLabel value={true} control={<Radio color="primary" disabled={isReadOnly} />} label="Yes" />
                              <FormControlLabel value={false} control={<Radio color="primary" disabled={isReadOnly} />} label="No" />
                            </RadioGroup>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
          {(segmentType === UserSegmentType.AutoUpdate || segmentType === UserSegmentType.Webhook || segmentType === UserSegmentType.Trigger) && (
            <Grid item xs={12}>
              <Grid container className={classes.row}>
                <Grid item xs={3}>
                  <Typography variant="body1">
                    Curation
                    <br />
                    Settings
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" component="div">
                      <Box>
                        <span>Is a greedy segment?</span>
                        <Help
                          title="Greedy Segment"
                          text="Once a user is tagged “greedy segment”, rest of the mappings between the user’s other segments and collections will be disregarded."
                        />
                      </Box>
                    </Typography>
                    <RadioGroup name="greedy" value={isGreedy} onChange={handleIsGreedyChange} className={classes.radioButtonGroups}>
                      <FormControlLabel value={true} control={<Radio color="primary" disabled={isReadOnly} />} label="Yes" />
                      <FormControlLabel value={false} control={<Radio color="primary" disabled={isReadOnly} />} label="No" />
                    </RadioGroup>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
          {segment && segmentType === UserSegmentType.Product && (
            <Grid item xs={12}>
              <Grid container className={classes.row}>
                <Grid item xs={3} className={classes.topAlignColumn}>
                  <Typography variant="body1">Products</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Grid container direction="column">
                    {segment.products?.map(item => (
                      <Grid key={`product-item-${item.id}`}>{item.productId}</Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
              <Grid container className={classes.row}>
                <Grid item xs={3} className={classes.topAlignColumn}>
                  <Typography variant="body1">Banner</Typography>
                </Grid>
                <Grid item xs={9}>
                  {segment.promotions && (
                    <Grid container direction="row">
                      <Grid item xs={6} className={classes.propItem}>
                        <Box className={classes.propTitle}>Main Store Banner</Box>
                        <Box className={classes.bannerBox} style={{ backgroundColor: segment.promotions.storeBannerColor }}>
                          <img src={segment.promotions.storeBanner} style={{ maxWidth: 200 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={6} className={classes.propItem}>
                        <Box className={classes.propTitle}>Instant Store Banner</Box>
                        <Box className={classes.bannerBox} style={{ backgroundColor: segment.promotions.instantBannerColor }}>
                          <img src={segment.promotions.instantBanner} style={{ maxWidth: 200 }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box className={classes.propTitle}>Starter Pack Product</Box>
                        <Box className={classes.propItem}>{segment.promotions.starterProduct}</Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box className={classes.propTitle}>Left Product</Box>
                        <Box className={classes.propItem}>{segment.promotions.leftProduct}</Box>
                        <Box className={classes.propTitle}>Left Product for Auto Re-filling Plan Subscribers(Do not Offer Auto Re-filling Plan)</Box>
                        <Box>{segment.promotions.leftProductForSubscription}</Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box className={classes.propTitle}>Right Product</Box>
                        <Box className={classes.propItem}>{segment.promotions.rightProduct}</Box>
                        <Box className={classes.propTitle}>Right Product for Auto Re-filling Plan Subscribers(Do not Offer Auto Re-filling Plan)</Box>
                        <Box>{segment.promotions.rightProductForSubscription}</Box>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container className={classes.row}>
              <Grid item xs={3} className={classes.topAlignColumn}>
                <Typography variant="body1">
                  Braze
                  <br />
                  Campaign
                  <br />
                  (optional)
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <Grid container direction="column">
                  <Grid item>
                    <Typography variant="body1" className={classes.maxAttendanceText} component="div">
                      <Box>
                        <span>Type</span>
                      </Box>
                      <Select
                        className={classes.mediumTextField}
                        value={brazeTriggerType}
                        onChange={handleOnchangeBrazeTriggerType}
                        name="brazeTriggerType"
                        displayEmpty
                        disabled={isSystemSegment || isReadOnly}
                      >
                        <MenuItem value="none" key="braze-type-none">
                          None
                        </MenuItem>
                        <MenuItem value="canvas" key="braze-type-canvas">
                          Canvas
                        </MenuItem>
                        <MenuItem value="campaign" key="braze-type-campaign">
                          Campaign
                        </MenuItem>
                      </Select>
                    </Typography>
                  </Grid>
                  {brazeTriggerType !== UserSegmentBrazeTriggerType.None && (
                    <React.Fragment>
                      <Grid item>
                        <Typography variant="body1" className={classes.maxAttendanceText} component="div">
                          <Box>
                            <span>{brazeTriggerType === UserSegmentBrazeTriggerType.Canvas ? 'Canvas' : 'Campaign'} ID</span>
                          </Box>
                          <TextField
                            className={classes.longTextField}
                            variant="outlined"
                            margin="dense"
                            value={brazeTriggerId}
                            disabled={isReadOnly}
                            onChange={handleOnchangeBrazeTriggerId}
                          />
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="body1" component="div">
                            <Box>
                              <span>Eligibility for reentering campaign</span>
                            </Box>
                          </Typography>
                          <RadioGroup
                            name="reentering"
                            value={brazeTriggerWhenReentering}
                            onChange={handleOnchangeBrazeTriggerWhenReentering}
                            className={classes.radioButtonGroups}
                          >
                            <FormControlLabel value={true} control={<Radio color="primary" disabled={isReadOnly} />} label="Yes" />
                            <FormControlLabel value={false} control={<Radio color="primary" disabled={isReadOnly} />} label="No" />
                          </RadioGroup>
                        </Box>
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      {segmentId && (
        <Box display={submenu === 'curation' ? 'block' : 'none'}>
          <CurationView
            homeType={homeType}
            segment={segment || ({} as IUserSegment)}
            mode={curationMode}
            isEditing={isEditing}
            featuredTitle={featuredTitle || ''}
            featuredCards={featuredCards}
            collections={collections}
            onChangeFeaturedTitle={handleOnChangeFeaturedTitle}
            onChangeFeaturedCards={handleOnChangeFeaturedCards}
            onChangeCollections={handleOnChangeCollections}
            onRefresh={fetchCurationData}
            onChangeHomeType={handleOnChangeHomeType}
          />
        </Box>
      )}
    </div>
  );
};

export default CreateUserSegment;
